import React, { useEffect, useRef, useState } from "react";
import { Table as ReactstrapTable, Badge } from "reactstrap";
import { compose } from "recompose";
import { withTable } from "components/common";
import { withXeroErrors, withDocumentRenderer } from "components";
import { Trans, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { findMajorityCurrency, format_reference, formatCurrency, formatDate, getSelectedAmount } from "services/util";
import Search from "./Search";
import { InvoiceDocument, SimpleInvoiceDocument } from "components/documents";
import { BulkDraftActions } from "../components/BulkDraftActions";
import { BulkVoidActions } from "../components/BulkVoidActions";
import { useOrganisationContext } from "hooks";
import { TruncateTd } from "components/common/Tables";

const EmailCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `invoice_${item.InvoiceNumber}.pdf`
    ),
  type: "SALES_INVOICE",
})(({ EmailDocument, DocumentRenderer, i, componentDoc, selectedOrganisation, simplified }) => (
  <>
    <EmailDocument compact simplified={simplified} item={i} type="SALES_INVOICE" selectedOrganisation={selectedOrganisation} />
    <DocumentRenderer component={componentDoc} />
  </>
));

const DownloadCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `invoice_${item.InvoiceNumber}.pdf`
    ),
  type: "SALES_INVOICE",
})(({ DownloadDocument, DocumentRenderer, i, componentDoc, selectedOrganisation, simplified }) => (
  <>
    <DownloadDocument simplified={simplified} compact item={i} className="mr-1" selectedOrganisation={selectedOrganisation} />
    <DocumentRenderer component={componentDoc} />
  </>
));

export const ReportingStatus = (zatca_report) => {
  const is_reported = zatca_report && (zatca_report?.status === 'Reported' || zatca_report?.cleared || zatca_report?.reported)
  const is_rejected = zatca_report && (zatca_report?.status === 'Rejected' || (!zatca_report?.cleared && !zatca_report?.reported))
  if (!is_reported && !is_rejected) return <Badge color="secondary" pill > <Trans>Not Verified</Trans></Badge>
  if (is_reported) return <Badge color="success" pill><Trans>Reported</Trans></Badge>
  if (is_rejected) return <Badge color="danger" pill><Trans>Rejected</Trans></Badge>
}

export const InvoiceTable = ({
  XeroErrors,
  Pagination,
  TableContainer,
  ViewingRecords,
  XeroTableDataFetch,
  data,
  setFilters,
  status,
  t,
  filters,
  selected,
  refreshData,
  calculationTotal,
  setData,
  ...props
}) => {
  const { selectedOrganisation } = useOrganisationContext()

  const [selectedDocType, setSelectedDocType] = useState(null);
  const [useSimpleType, setSimpleType] = useState(false);
  const selectedType = useSimpleType ? SimpleInvoiceDocument : InvoiceDocument;
  const initialFilters = useRef(filters);

  const selectedList = Object.keys(selected).filter(key => selected[key]);

  const selectedInvoiceList = data?.results?.filter(item => selectedList.includes(item.InvoiceID))

  const selectedData = selectedInvoiceList?.map(item => ({ Total: parseFloat(item.Total), SubTotal: parseFloat(item.SubTotal) }));
  // const totalSubTotal = formatCurrency(selectedData.reduce((acc, item) => acc + item.SubTotal, 0));
  const totalTotal = formatCurrency(selectedData.reduce((acc, item) => acc + item.Total, 0));

  const majorityCurrency = findMajorityCurrency(data.results);
  const selectedTotalMessage = getSelectedAmount(selectedList?.length, "Invoice", totalTotal, majorityCurrency);
  // data = data.map((el) => {})

  useEffect(() => {
    if (data?.results.length) {
      const _data = data?.results.map(
        (item) => ({
          ...item,
          Reference: format_reference(item.Reference),
          DocumentType: item.DocumentType || "standard",
        })
      )
      setData({ ...data, results: _data })
    }
  }, [data?.results.length])


  return (
    <div className="inv-table">
      <XeroErrors color="warning" />
      <Search
        onFiltersChanged={(v) => {
          setFilters(v === null ? initialFilters.current : v);
        }}
        invoice
        status={status}
        exportTypeShow={true}
        setSimpleType={setSimpleType}
        useSimpleType={useSimpleType}
        selectedItems={selectedInvoiceList}
        selectedOrganisation={selectedOrganisation}
        isInvoice={true}
        setSelectedDocType={setSelectedDocType}
      />
      <div className="row my-3">
        <div className="col-6 text-left">
          {status === "DRAFT" && (
            <BulkDraftActions
              onSuccess={refreshData}
              items={data.results}
              url="invoices"
              pk="InvoiceID"
              selected={selected}
              className="ml-0"
            />
          )}
          {status === "AUTHORISED" && (
            <BulkVoidActions
              onSuccess={refreshData}
              items={data.results}
              url="invoices"
              pk="InvoiceID"
              selected={selected}
            />
          )}
          {status === "PAID" && (
            <BulkVoidActions
              onSuccess={refreshData}
              items={data.results}
              url="invoices"
              pk="InvoiceID"
              selected={selected}
            />
          )}
        </div>
        <div className="col-6 text-right">
          <div className="text-muted">
            <span>{selectedList.length > 0 ? (selectedTotalMessage) : (calculationTotal)}</span>
          </div>
        </div>
      </div>
      <div className="small">
        <TableContainer idKey="InvoiceID">
          <ReactstrapTable responsive size="sm" className="small">
            <thead>
              <tr>
                <th select />
                <th sortBy="InvoiceNumber" className="text-nowrap">
                  <Trans>Number</Trans>
                </th>
                <th sortBy="Reference" className="text-nowrap">
                  <Trans>Reference</Trans>
                </th>
                <th sortBy="Contact.Name" className="text-nowrap">
                  <Trans>To</Trans>
                </th>
                <th className="text-center text-nowrap">
                  <Trans>Type</Trans>
                </th>
                <th sortBy="Date" className="text-nowrap">
                  <Trans>Date</Trans>
                </th>
                <th sortBy="DueDate" className="text-nowrap">
                  <Trans>Due Date</Trans>
                </th>
                <th className="text-nowrap">
                  <Trans>Clearance</Trans>
                </th>
                <th className="text-right text-nowrap" sortBy="AmountPaid">
                  <Trans>Paid</Trans>
                </th>
                <th className="text-right text-nowrap" sortBy="AmountDue">
                  <Trans>Due</Trans>
                </th>
                <th sortBy="Status" className="text-nowrap">
                  <Trans>Status</Trans>
                </th>
                {/* <th sortBy="SentToContact" className="text-nowrap">
                  <Trans>Sent</Trans>
                </th> */}
                <th className="text-nowrap">
                  <i className="far fa-file"></i>
                </th>
                <th className="text-nowrap">
                  <Trans>Actions</Trans>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.loading && (
                <tr>
                  <td colSpan="9" className="text-center">
                    <span className="fa fa-spinner fa-spin mt-2" /> <Trans>Loading...</Trans>
                  </td>
                </tr>
              )}
              {data?.results?.filter((i) => selectedDocType? (selectedDocType==='undefined' ? !i.DocumentType : i.DocumentType === selectedDocType) : true).map((i, idx) => (
                <tr key={i.InvoiceID}>
                  <td select />
                  <TruncateTd content={i.InvoiceNumber} _key={`Number_${idx}`} className="truncate">
                    <Link
                      to={`/accounts_receivable/invoices/view/${i.InvoiceID}`}
                    >
                      {i.InvoiceNumber}
                    </Link>
                  </TruncateTd>
                  <TruncateTd content={i.Reference} _key={`reference_${idx}`} className="truncate"> {i.Reference || t("-")} </TruncateTd>
                  <TruncateTd content={i.Contact?.Name} _key={`contact_${idx}`} className="truncate">
                    {i.Contact &&
                      <Link to={`/contacts/view/${i.Contact.ContactID}`} >
                        {i.Contact.Name}
                      </Link>
                    }
                  </TruncateTd> 
                  <td className="text-center text-capitalize font-weight-normal">{i.DocumentType || "-"}</td>
                  <td>{formatDate(i.Date)}</td>
                  <td>{formatDate(i.DueDate)}</td>
                  <td>{ReportingStatus(i.Reporting)}</td>
                  <td className="text-right">{i.AmountPaid === 0 && i.Status === "PAID" && i.AmountDue === 0 ? (formatCurrency(i.Total)) : (formatCurrency(i.AmountPaid))}</td>
                  <td className="text-right">{formatCurrency(i.AmountDue)}</td>
                  <td>{i.Status}</td>
                  <td><Link to={`/accounts_receivable/invoices/view/${i.InvoiceID}`}>{i.HasAttachments ? <span className="fa fa-file" /> : "-"}</Link></td>
                  <td>
                    <div className="row">
                      <div className="col px-0 mb-1 ml-1">
                        <DownloadCell i={i} simplified={useSimpleType} componentDoc={i.DocumentType === 'standard'? InvoiceDocument: SimpleInvoiceDocument} selectedOrganisation={selectedOrganisation} />
                      </div>
                      <div className="col px-0">
                        <EmailCell i={i} simplified={useSimpleType} componentDoc={i.DocumentType === 'standard'? InvoiceDocument: SimpleInvoiceDocument} selectedOrganisation={selectedOrganisation} />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ReactstrapTable>
        </TableContainer>
      </div>
      <XeroTableDataFetch />
      <div className="float-right">
        <Pagination size="sm" count={0} noPages />
      </div>
      <div className="clearfix">
        <ViewingRecords noCount className="float-left" />
      </div>

    </div>
  );
};

export const enhance = compose(
  withTranslation(),
  withXeroErrors(),
  withTable({
    limit: 50,
    url: "invoices",
    ordering: {
      column: "Date",
      direction: 2,
    },
  }),
  withDocumentRenderer({
    onDownload:
      ({ xero }) =>
        (getDocument, item) => {
          getDocument({ item }, `invoice_${item.InvoiceNumber}.pdf`);
        },
  })
);

export default enhance(InvoiceTable);
